import axios from 'axios';

const baseURL = (import.meta.env.BASE_URL || '').replace(/\/$/, '');

const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = `${import.meta.env.BASE_URL}login`;
        }
        return Promise.reject(error);
    }
);

export default api;

// API functions
export const authAPI = {
    login: (email, password) => {
        const params = new URLSearchParams();
        params.append('username', email);
        params.append('password', password);
        return api.post('/api/auth/login', params, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
    },
    register: (data) => api.post('/api/auth/register', data),
    me: () => api.get('/api/auth/me'),
    getResearchSettings: () => api.get('/api/auth/research-settings'),
    updateResearchSettings: (data) => api.put('/api/auth/research-settings', data)
};

export const blogsAPI = {
    getAll: () => api.get('/api/blogs'),
    getOne: (id) => api.get(`/api/blogs/${id}`),
    create: (data) => api.post('/api/blogs', data),
    delete: (id) => api.delete(`/api/blogs/${id}`),
    getPrompts: (id) => api.get(`/api/blogs/${id}/prompts`),
    updatePrompts: (id, data) => api.put(`/api/blogs/${id}/prompts`, data),
    getPersona: (id) => api.get(`/api/blogs/${id}/persona`),
    updatePersona: (id, data) => api.put(`/api/blogs/${id}/persona`, data),
    deletePersona: (id) => api.delete(`/api/blogs/${id}/persona`),
    getPromos: (id) => api.get(`/api/blogs/${id}/promos`),
    createPromo: (id, data) => api.post(`/api/blogs/${id}/promos`, data),
    getPromptTemplates: (id) => api.get(`/api/blogs/${id}/prompt-templates`),
    updatePromptTemplate: (id, data) => api.put(`/api/blogs/${id}/prompt-templates`, data),
    // Image Styles CRUD
    getImageStyles: (id) => api.get(`/api/blogs/${id}/image-styles`),
    createImageStyle: (blogId, data) => api.post(`/api/blogs/${blogId}/image-styles`, data),
    updateImageStyle: (blogId, styleId, data) => api.put(`/api/blogs/${blogId}/image-styles/${styleId}`, data),
    deleteImageStyle: (blogId, styleId) => api.delete(`/api/blogs/${blogId}/image-styles/${styleId}`),
    previewStyle: (blogId, styleId) => api.post(`/api/blogs/${blogId}/image-styles/${styleId}/preview`),
    getStylePreview: (blogId, styleId, previewId) => api.get(`/api/blogs/${blogId}/image-styles/${styleId}/previews/${previewId}`),
    styleFeedback: (blogId, styleId, data) => api.post(`/api/blogs/${blogId}/image-styles/${styleId}/feedback`, data),
    applyStyle: (blogId, styleId, data) => api.post(`/api/blogs/${blogId}/image-styles/${styleId}/apply`, data)
};

export const generationAPI = {
    getProviders: () => api.get('/api/generate/providers'),
    generateArticle: (data) => api.post('/api/generate/article', data),
    generateImages: (data) => api.post('/api/generate/images', data),
    regenerateImage: (imageId, data) => api.post(`/api/generate/images/${imageId}/regenerate`, data),
    upscaleImages: (articleId, data) => api.post(`/api/generate/articles/${articleId}/images/upscale`, data),
    getRealImageEditSuggestions: (imageId) => api.get(`/api/generate/real-images/${imageId}/edit-suggestions`),
    editRealImage: (imageId, data) => api.post(`/api/generate/real-images/${imageId}/edit`, data),
    getArticles: (blogId = null) => {
        const config = blogId ? { params: { blog_id: blogId } } : undefined;
        return api.get('/api/generate/articles', config);
    },
    getArticle: (id) => api.get(`/api/generate/articles/${id}`),
    updateArticle: (id, data) => api.patch(`/api/generate/articles/${id}`, data),
    confirmNews: (articleId, body) => api.post(`/api/generate/articles/${articleId}/confirm-news`, body),
    downloadImage: (id) => api.get(`/api/generate/images/${id}/download`, { responseType: 'arraybuffer' }),
    markViewed: (articleId) => api.post(`/api/generate/articles/${articleId}/mark-viewed`),
    downloadZip: (articleId) => api.get(`/api/generate/articles/${articleId}/images/download-all`, { responseType: 'blob' }),
};

export const templatesAPI = {
    getAll: (templateType = null) => api.get('/api/templates', {
        params: templateType ? { template_type: templateType } : {}
    }),
    getOne: (id) => api.get(`/api/templates/${id}`),
    create: (data) => api.post('/api/templates', data),
    update: (id, data) => api.put(`/api/templates/${id}`, data),
    delete: (id) => api.delete(`/api/templates/${id}`)
};

export const promptTemplatesAPI = {
    list: (templateType = null) => api.get('/api/prompt-templates', {
        params: templateType ? { template_type: templateType } : {}
    }),
    create: (data) => api.post('/api/prompt-templates', data),
    update: (id, data) => api.put(`/api/prompt-templates/${id}`, data),
    delete: (id) => api.delete(`/api/prompt-templates/${id}`)
};

export const logsAPI = {
    getAll: (action = null) => api.get('/api/logs', {
        params: action ? { action } : {}
    }),
    getOne: (id) => api.get(`/api/logs/${id}`),
    download: (id) => api.get(`/api/logs/${id}/download`, { responseType: 'blob' })
};

export const utmAPI = {
    getTableTemplates: () => api.get('/api/utm/table-templates'),
    createTableTemplate: (data) => api.post('/api/utm/table-templates', data),
    getColumnPresets: () => api.get('/api/utm/column-presets'),
    createColumnPreset: (data) => api.post('/api/utm/column-presets', data),
    deleteTableTemplate: (id) => api.delete(`/api/utm/table-templates/${id}`),
    deleteColumnPreset: (id) => api.delete(`/api/utm/column-presets/${id}`),
    getHistory: () => api.get('/api/utm/history'),
    getHistoryEntry: (id) => api.get(`/api/utm/history/${id}`),
    downloadHistory: (id) => api.get(`/api/utm/history/${id}/download`, { responseType: 'blob' }),
    generateDataset: (formData) => api.post('/api/utm/generate', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
};

export const leadpagesAPI = {
    createSource: (data) => api.post('/api/leadpages/sources', data),
    getSources: () => api.get('/api/leadpages/sources'),
    getSource: (id) => api.get(`/api/leadpages/sources/${id}`),
    deleteSource: (id) => api.delete(`/api/leadpages/sources/${id}`),
    planPack: (id) => api.post(`/api/leadpages/sources/${id}/plan`),
    generatePack: (id) => api.post(`/api/leadpages/sources/${id}/generate`),
    getLeadpages: (id) => api.get(`/api/leadpages/sources/${id}/leadpages`),
    getLeadpageHtml: (pageId) => api.get(`/api/leadpages/pages/${pageId}/html`, { responseType: 'text' }),
    updateLeadpage: (pageId, data) => api.patch(`/api/leadpages/pages/${pageId}`, data),
    getLeadpage: (pageId) => api.get(`/api/leadpages/pages/${pageId}`),
    editLeadpage: (pageId, request) => api.post(`/api/leadpages/pages/${pageId}/edit`, { request }),
    getVersions: (pageId) => api.get(`/api/leadpages/pages/${pageId}/versions`),
    getVersionHtml: (pageId, versionId) => api.get(`/api/leadpages/pages/${pageId}/versions/${versionId}/html`, { responseType: 'text' }),
    activateVersion: (pageId, versionId) => api.post(`/api/leadpages/pages/${pageId}/versions/${versionId}/activate`),
    getManage: () => api.get('/api/leadpages/manage'),
    duplicateLeadpage: (pageId) => api.post(`/api/leadpages/pages/${pageId}/duplicate`),
    exportLeadpage: (pageId) => api.get(`/api/leadpages/pages/${pageId}/export`, { responseType: 'blob' }),
    deleteLeadpage: (pageId) => api.delete(`/api/leadpages/pages/${pageId}`),
    getDeployAdapters: () => api.get('/api/leadpages/deploy/adapters'),
    getDomains: () => api.get('/api/leadpages/domains'),
    createDomain: (data) => api.post('/api/leadpages/domains', data),
    updateDomain: (domainId, data) => api.patch(`/api/leadpages/domains/${domainId}`, data),
    deleteDomain: (domainId) => api.delete(`/api/leadpages/domains/${domainId}`)
};
